// ContactFormSection.js
import React, { useState } from "react";
import { Phone, Clock, MapPin } from "lucide-react";

const ContactFormSection = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    interest: "",
    consent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mock submission with 3-second delay
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);

      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        interest: "",
        consent: false,
      });

      // Hide success message after 5 seconds
      setTimeout(() => {
        setIsSuccess(false);
      }, 5000);
    }, 3000);
  };

  return (
    <section className="py-16 px-6 md:px-12 lg:px-24 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Contact Form */}
        <div className="max-w-3xl mx-auto mb-20">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-8">
            Send Us a Message or Schedule a Callback
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                name="firstName"
                placeholder="First Name*"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
              />
              <input
                type="text"
                name="lastName"
                placeholder="Last Name*"
                value={formData.lastName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="email"
                name="email"
                placeholder="Email*"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
              />
              <input
                type="tel"
                name="phone"
                placeholder="Phone*"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
              />
            </div>

            <select
              name="interest"
              value={formData.interest}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent text-gray-500"
            >
              <option value="">I'm interested in...</option>
              <option value="alzheimers-dementia">
                Alzheimer's and Dementia Care
              </option>
              <option value="companionship">Companionship Care</option>
              <option value="hospice">Hospice Care</option>
              <option value="personal">Personal Care</option>
              <option value="post-operative">Post-Operative Care</option>
              <option value="respite">Respite Care</option>
              <option value="specialty">Specialty Care</option>
              <option value="spinal-cord">Spinal Cord Injury Care</option>
              <option value="general">General Inquiry</option>
            </select>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                name="consent"
                id="consent"
                checked={formData.consent}
                onChange={handleChange}
                className="mt-1 w-4 h-4 text-[#E85D2C] border-gray-300 rounded focus:ring-[#E85D2C]"
              />
              <label
                htmlFor="consent"
                className="text-sm text-gray-700 font-light"
              >
                By providing a telephone number and submitting this form, you
                are consenting to be contacted by SMS text message. Message &
                data rates may apply. Message frequency may vary.{" "}
                <a
                  href="/privacy-policy"
                  className="text-[#E85D2C] hover:underline"
                >
                  Privacy Policy
                </a>
                . Reply Help for more information. You can reply STOP to opt-out
                of further messaging
              </label>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#E85D2C] hover:bg-[#d54d1f] text-white font-semibold py-4 rounded-full transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>

            {isSuccess && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 font-medium text-center">
                  Thank you for your message! We have received your inquiry and
                  will contact you shortly.
                </p>
              </div>
            )}
          </form>
        </div>

        {/* Get In Touch Section */}
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-px bg-[#E85D2C] w-24"></div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Get In Touch With
              </h2>
              <div className="h-px bg-[#E85D2C] w-24"></div>
            </div>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900">
              Allawah Lodge
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {/* Phone */}
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 border-2 border-[#AD2647] rounded-full flex items-center justify-center mb-4">
                <Phone className="w-7 h-7 text-[#AD2647]" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Phone</h4>
              <a
                href="tel:+61269272706"
                className="text-gray-700 hover:text-[#E85D2C] transition-colors"
              >
                (02) 6927 2706
              </a>
            </div>

            {/* Location */}
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 border-2 border-[#AD2647] rounded-full flex items-center justify-center mb-4">
                <MapPin className="w-7 h-7 text-[#AD2647]" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Coolamon</h4>
              <p className="text-gray-700 text-sm">
                Cnr Mirrool & Stinson St,
                <br />
                Suite 250
                <br />
                Coolamon, NSW 2701
              </p>
            </div>

            {/* Office Hours */}
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 border-2 border-[#AD2647] rounded-full flex items-center justify-center mb-4">
                <Clock className="w-7 h-7 text-[#AD2647]" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">
                Office Hours
              </h4>
              <p className="text-gray-700 text-sm">
                8:30 a.m. – 5:00 p.m.
                <br />
                Monday – Friday
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactFormSection;
